/**
 * External dependencies
 */
import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Divider,
  Stack,
} from "@towersystems/towersystems-design-system";
import { connect } from "react-redux";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { get, map, size } from "lodash";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import page from "page";

/**
 * Internal dependencies
 */
import {
  Layout,
  Topbar,
  Content,
  Sidebar,
} from "modules/report-builder/layout";
import Button from "components/button";
import Icon from "components/icon";
import { editReport } from "state/reports/actions";
import { getBuilderReportId } from "state/ui/report-builder/selectors";
import MeasuresOverlay from "./measures-overlay";
import DimensionsOverlay from "./dimensions-overlay";
import FiltersOverlay from "./filters-overlay";
import { getEditedReport } from "state/reports/selectors";

import MoveableStack from "components/moveable-stack";
import ReportPreview from "modules/report-builder/report-preview";
import EditReportName from "modules/report-builder/edit-report-title";
import {
  removeMeasure,
  removeDimension,
} from "state/ui/report-builder/actions";
import SelectChartType from "modules/report-builder/select-chart-type";
import ChartConfiguration from "modules/report-builder/chart-configuration";
import ColorSchemeDropdown from "modules/report-builder/color-scheme-dropdown";
import useReportBuilder from "modules/report-builder/use-report-builder";
import { getPreviousPath } from "state/ui/selectors";
import ConfigureDisableTimeFilter from "modules/report-builder/chart-configuration/base/configure-disable-time-filter";

/**
 * Style dependencies
 */
import "./styles.scss";
import TimeOverlay from "./time-overlay";
import removeFilter from "../../../state/ui/report-builder/actions/remove-filter";

const DataStep = ({
  reportId,
  goToStep,
  previousPath,
  measures,
  chartType,
  filters,
  dimensions,
  goToNextStep,
  report,
  removeFilter,
  selectedTimeDimension,
  selectedGranularity,
  selectedDateRange,
  ...props
}) => {
  const [showDimensionsOverlay, setShowDimensionsOverlay] = useState(false);
  const [showMeasuresOverlay, setShowMeasuresOverlay] = useState(false);
  const [showFiltersOverlay, setShowFiltersOverlay] = useState(false);
  const [showTimeOverlay, setShowTimeOverlay] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);

  const dataType = get(report, "config.dataType");

  const { isSaving, saveReport } = useReportBuilder();

  const goFilterOverlay = (filter) => {
    setActiveFilter(filter);
    setShowFiltersOverlay(true);
  };
  const handleSave = async () => {
    try {
      await saveReport();
      page(previousPath);
    } catch (error) {
      alert("failed to save");
    }
  };

  return (
    <Layout>
      {({ classes, innerWrapperClasses }) => {
        return (
          <Box className={classes}>
            <Topbar>
              <>
                <Button link={previousPath} lined>
                  Exit
                </Button>
                <EditReportName />
                <Box>
                  <Button secondary onClick={handleSave} disabled={isSaving}>
                    Save
                  </Button>
                </Box>
              </>
            </Topbar>
            <Box className={innerWrapperClasses}>
              <Sidebar spacing={null}>
                <DndProvider backend={HTML5Backend}>
                  <Tabs>
                    <TabList>
                      <Tab>Data</Tab>
                      <Tab>Visualization</Tab>
                    </TabList>
                    <TabPanel>
                      <Box padding="large">
                        <Box marginBottom="large">
                          <Heading level="4">Measures</Heading>
                        </Box>
                        <Text size="small">
                          Add metrics to build this report with the data you
                          want.
                        </Text>
                        {size(measures) >= 1 && (
                          <Box marginBottom="medium" marginTop="large">
                            <Box marginBottom="medium">
                              <Text size="small" weight="medium">
                                Selected measures
                              </Text>
                            </Box>
                            <MoveableStack
                              onChange={(measures) =>
                                props.editReport(reportId, {
                                  config: { measures: map(measures, "id") },
                                })
                              }
                              items={map(measures, (measure) => ({
                                key: measure,
                                label: measure,
                                id: measure,
                                remove: (id) => props.removeMeasure(id),
                              }))}
                            />
                          </Box>
                        )}
                        <Button
                          compact
                          onClick={() => setShowMeasuresOverlay(true)}
                        >
                          Add measure
                        </Button>
                      </Box>
                      <Divider />
                      <Box padding="large">
                        <Box marginBottom="large">
                          <Heading level="4">Dimensions</Heading>
                        </Box>
                        <Text size="small">
                          Add dimension to build this report with the data you
                          want.
                        </Text>
                        {size(dimensions) >= 1 && (
                          <Box marginBottom="medium" marginTop="large">
                            <Box marginBottom="medium">
                              <Text size="small" weight="medium">
                                Selected dimensions
                              </Text>
                            </Box>
                            <MoveableStack
                              onChange={(dimensions) =>
                                props.editReport(reportId, {
                                  config: { measures: map(dimensions, "id") },
                                })
                              }
                              items={map(dimensions, (dimension) => ({
                                key: dimension,
                                label: dimension,
                                id: dimension,
                                remove: (id) => props.removeDimension(id),
                              }))}
                            />
                          </Box>
                        )}
                        <Box marginTop="large">
                          <Button
                            compact
                            onClick={() => setShowDimensionsOverlay(true)}
                          >
                            Add dimension
                          </Button>
                        </Box>
                      </Box>
                      <Divider />
                      <Box padding="large">
                        <Box marginBottom="large">
                          <Heading level="4">Time Dimension</Heading>
                        </Box>
                        <Text size="small">
                          Select which time dimension to use for filtering and
                          grouping data. Closest matching time dimension will be
                          selected by default if not specified*.
                        </Text>
                        {selectedTimeDimension ? (
                          <Box
                            component="button"
                            padding="large"
                            className="ui-list__item"
                            position="relative"
                          >
                            <span
                              className="dashboard-builder__filter-measure-label"
                              onClick={() => setShowTimeOverlay(true)}
                            >
                              {`${selectedTimeDimension}`}
                            </span>
                            {` ${selectedDateRange ? "Default" : ""} ${selectedGranularity ? selectedGranularity : ""}`}
                            <Box className="dashboard-builder__delete-filter-btn-wrapper">
                              <Button
                                className="dashboard-builder__delete-filter-btn"
                                onClick={() =>
                                  props.editReport(reportId, {
                                    config: {
                                      timeDimension: null,
                                      timeGranularity: null,
                                      timeDateRange: null,
                                    },
                                  })
                                }
                              >
                                <Icon name="FiTrash" />
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <Box marginTop="large">
                            <Button
                              compact
                              onClick={() => setShowTimeOverlay(true)}
                            >
                              Select Time Dimension
                            </Button>
                          </Box>
                        )}
                      </Box>
                      <Divider />
                      <Box padding="large">
                        <Box marginBottom="large">
                          <Heading level="4">Filters</Heading>
                        </Box>
                        <Text size="small">
                          Add filters to narrow down your data.
                        </Text>
                        {size(filters) >= 1 && (
                          <Box marginBottom="medium" marginTop="large">
                            <Stack space="medium">
                              {map(filters, (filter, index) => {
                                return (
                                  <Box
                                    component="button"
                                    padding="large"
                                    className="ui-list__item"
                                    position="relative"
                                  >
                                    <span
                                      className="dashboard-builder__filter-measure-label"
                                      onClick={() => goFilterOverlay(filter)}
                                    >
                                      {`${filter.member}`}
                                    </span>
                                    {`${filter.operator} ${filter?.values?.[0]}`}
                                    <Box className="dashboard-builder__delete-filter-btn-wrapper">
                                      <Button
                                        className="dashboard-builder__delete-filter-btn"
                                        onClick={() => removeFilter(index)}
                                      >
                                        <Icon name="FiTrash" />
                                      </Button>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                        <Button compact onClick={() => goFilterOverlay({})}>
                          Add Filter
                        </Button>
                      </Box>

                      <>
                        <Divider />
                        <Box padding="large">
                          <Box marginBottom="large">
                            <Heading level="4">Other Settings</Heading>
                          </Box>
                          <Text size="small">Customize Data Settings</Text>
                          <Box marginBottom="medium" marginTop="large">
                            <Stack space="medium">
                              <ConfigureDisableTimeFilter />
                            </Stack>
                          </Box>
                        </Box>
                      </>
                    </TabPanel>
                    <TabPanel>
                      <Box padding="large">
                        <SelectChartType />
                        <Divider />
                        <ChartConfiguration chartType={chartType} />
                      </Box>
                    </TabPanel>
                  </Tabs>
                  {showMeasuresOverlay && (
                    <MeasuresOverlay
                      goBack={() => setShowMeasuresOverlay(false)}
                    />
                  )}
                  {showDimensionsOverlay && (
                    <DimensionsOverlay
                      goBack={() => setShowDimensionsOverlay(false)}
                    />
                  )}
                  {showTimeOverlay && (
                    <TimeOverlay goBack={() => setShowTimeOverlay(false)} />
                  )}

                  {showFiltersOverlay && (
                    <FiltersOverlay
                      filter={activeFilter}
                      goBack={() => setShowFiltersOverlay(false)}
                    />
                  )}
                </DndProvider>
              </Sidebar>
              <Content>
                <Box display="flex" flexDirection="column" width="full">
                  <Box
                    display="flex"
                    justifyContent="flexEnd"
                    paddingRight="large"
                  >
                    <ColorSchemeDropdown />
                  </Box>
                  <ReportPreview />
                </Box>
              </Content>
            </Box>
          </Box>
        );
      }}
    </Layout>
  );
};

export default connect(
  (state) => {
    let reportId = getBuilderReportId(state);
    let report = getEditedReport(state, reportId);

    console.log("reportId", report);

    let measures = (report && report.config.measures) || [];
    let dimensions = (report && report.config.dimensions) || [];
    let chartType = (report && report.chartType) || report.chart_type;
    let filters = (report && report.config.filters) || [];

    let selectedTimeDimension = get(report, "config.timeDimension");
    let selectedDateRange = get(report, "config.timeDateRange");
    let selectedGranularity = get(report, "config.timeGranularity");

    return {
      reportId,
      report,
      measures,
      dimensions,
      chartType,
      filters,
      selectedTimeDimension,
      selectedGranularity,
      selectedDateRange,
      previousPath: getPreviousPath(state),
    };
  },
  {
    editReport,
    removeMeasure,
    removeDimension,
    removeFilter,
  }
)(DataStep);
